// REACT
import { useState, useContext, useEffect } from "react";

// MUI
import {
	Box,
	Divider,
	FormControl,
	InputAdornment,
	InputLabel,
	OutlinedInput,
	Skeleton,
	TextField,
	Typography,
	useTheme,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// MUI ICONS
import GoogleIcon from "@mui/icons-material/Google";
import WindowIcon from "@mui/icons-material/Window";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// LIBRARIES
import { useTranslation } from "react-i18next";

// Files
import {
	gettingCompanyLogo,
	siginingIn,
	validatePassword,
	validateUsername,
} from "../../utils/Login";
import { GlobalContext } from "../../GlobalContext";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../utils/session/authConfig";
import { useGoogleLogin } from "@react-oauth/google";
import pkg from "../../../package.json";

export const Login = ({ redirect }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation('sign_in');
  const { instance } = useMsal();

	const { setAuthenticated, handlerAlert } = useContext(GlobalContext);

	const [username, setUsername] = useState({
		value: "",
		error: false,
		helperText: "",
	});

	const [password, setPassword] = useState({
		value: "",
		error: false,
		helperText: "",
	});

	const [visibility, setVisibility] = useState(false);

	const [loading, setLoading] = useState(false);
	const [loadingLogo, setLoadingLogo] = useState(true);

	const handleLoginRedirect = () => {
		instance
			.loginRedirect({
				...loginRequest,
				prompt: "create",
				redirectStartPage: window.location.origin,
			})
			.catch((error) => console.error(error));
	};

	const signing = useGoogleLogin({
		onSuccess: (tokenResponse) => {
			//console.log(tokenResponse);
			sessionStorage.setItem("access_Token", JSON.stringify(tokenResponse));
		},
		flow: "auth-code",
		ux_mode: "redirect",
		redirect_uri: "http://localhost:3000",
		onError: (error) => console.error(error),
	});

	useEffect(() => {
		gettingCompanyLogo(setLoadingLogo, theme.palette.mode);

		if (sessionStorage.getItem("403")) {
			handlerAlert(t("sign_in.alerts.403.title"), t("sign_in.alerts.403.message"), "error");

			sessionStorage.removeItem("403");
		}
	}, [handlerAlert, t, theme.palette.mode]);

	const showGoogleLogin = window.location.hostname.split(".")[0] !== "fp";

	return (
		<Box
			sx={{
				width: 400,
				p: 7,
				borderRadius: 5,
				border: 1,
				borderColor: "secondary.main",
			}}
		>
			<form
				onSubmit={(event) => {
					siginingIn(
						event,
						setLoading,
						username,
						password,
						setAuthenticated,
						setUsername,
						setPassword,
						t("sign_in.form.invalid_credentials"),
						i18n,
						redirect,
					);
				}}
			>
				<Box
					sx={{
						width: "100%",
						height: 90,
						mt: 2,
						mb: 4,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
					}}
				>
					{loadingLogo ? (
						<Skeleton
							variant="rectangular"
							sx={{
								width: "100%",
								height: "100%",
								borderRadius: 1,
							}}
						/>
					) : (
						<Box
							component="img"
							src={JSON.parse(sessionStorage.getItem("images"))?.logo_login}
							alt="logo-app"
							sx={{
								width: "100%",
								height: "100%",
								objectFit: "contain",
								display: "block",
							}}
						/>
					)}
				</Box>

				<TextField
					id="username"
					label={t("sign_in.form.username")}
					type="text"
					variant="outlined"
					autoFocus={true}
					fullWidth
					value={username.value}
					onChange={(event) => {
						if (validateUsername(event.target.value)) {
							setUsername({
								value: event.target.value,
								error: false,
								helperText: "",
							});
						} else {
							setUsername({
								value: event.target.value,
								error: true,
								helperText: event.target.value
									? t("sign_in.form.invalid_username")
									: t("sign_in.form.empty_username"),
							});
						}
					}}
					error={username.error}
					helperText={username.helperText}
				/>

				<FormControl sx={{ mt: 2 }} fullWidth>
					<InputLabel htmlFor="password">{t("sign_in.form.password")}</InputLabel>

					<OutlinedInput
						id="password"
						type={visibility ? "text" : "password"}
						label={t("sign_in.form.password")}
						variant="outlined"
						fullWidth
						value={password.value}
						onChange={(event) => {
							if (validatePassword(event.target.value)) {
								setPassword({
									value: event.target.value,
									error: false,
									helperText: "",
								});
							} else {
								setPassword({
									value: event.target.value,
									error: true,
									helperText: event.target.value
										? t("sign_in.form.invalid_password")
										: t("sign_in.form.empty_password"),
								});
							}
						}}
						endAdornment={
							<InputAdornment position="end">
								{visibility ? (
									<VisibilityOffIcon sx={{ cursor: "pointer" }} onClick={() => setVisibility(false)} />
								) : (
									<VisibilityIcon sx={{ cursor: "pointer" }} onClick={() => setVisibility(true)} />
								)}
							</InputAdornment>
						}
						error={password.error}
					/>
				</FormControl>

				{password.helperText && (
					<Typography variant="caption" color="error" sx={{ mt: 0.75, ml: 1.75, display: "block" }}>
						{password.helperText}
					</Typography>
				)}

				<LoadingButton
					sx={{ mt: 3, mb: 3, height: 50 }}
					type="submit"
					fullWidth
					variant="contained"
					color="primary"
					loading={loading}
					disableElevation
					disabled={username.error || password.error || username.value === "" || password.value === ""}
				>
					{t("sign_in.form.sign_in")}
				</LoadingButton>

				<Divider>
					<Typography>{t("sign_in.form.or")}</Typography>
				</Divider>

				{showGoogleLogin && (
					<LoadingButton
						sx={{ mt: 3, height: 50 }}
						fullWidth
						variant="contained"
						color="primary"
						data-tone="dark"
						loading={false}
						disableElevation
						endIcon={<GoogleIcon />}
						onClick={() => signing()}
						disabled={true}
					>
						{t("sign_in.form.sign_in_with")}
					</LoadingButton>
				)}

				<LoadingButton
					sx={{ mt: 3, height: 50 }}
					fullWidth
					variant="contained"
					color="primary"
					data-tone="dark"
					loading={false}
					disableElevation
					endIcon={<WindowIcon />}
					onClick={() => handleLoginRedirect()}
				>
					{t("sign_in.form.sign_in_with")}
				</LoadingButton>

				<Typography sx={{ mt: 4, textAlign: "center" }}>
					{t("sign_in.form.version")}: {pkg.version}
				</Typography>
			</form>
		</Box>
	);
};
